import React, {useEffect, useRef, useState} from 'react';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import XYZ from 'ol/source/XYZ';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import Icon from 'ol/style/Icon';
import Style from 'ol/style/Style';
import Text from 'ol/style/Text';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import {fromLonLat} from 'ol/proj';
import axios from 'axios';
import MarkerCard from './map/MarkerCard';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import {BackButton} from "./HookUtils";
import {BasketComponent} from "./BasketComponent";
import {MARKET_BASE_URL_API} from "../Const";

const defaultMarketImage = '/default.png';
const apiUrl = '/api/v3/search';

export default function FixedMapWithMarkets() {
    const mapRef = useRef();
    const olMapRef = useRef(null);
    const markerLayerRef = useRef(null);
    const cardRefs = useRef([]);
    const [markets, setMarkets] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState(null);
    const [query, setQuery] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [userLocation, setUserLocation] = useState(null);

    useEffect(() => {
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                setUserLocation({
                    lat: pos.coords.latitude,
                    lon: pos.coords.longitude
                });
            },
            () => setUserLocation(null),
            {enableHighAccuracy: true, timeout: 10000}
        );
    }, []);

    useEffect(() => {
        if (!olMapRef.current && mapRef.current) {
            olMapRef.current = new Map({
                target: mapRef.current,
                layers: [
                    new TileLayer({source: new XYZ({url: 'https://{a-c}.tile.openstreetmap.org/{z}/{x}/{y}.png'})})
                ],
                view: new View({
                    center: fromLonLat([50.8627728, 32.3516475]),
                    zoom: 14
                })
            });
        }
    }, []);

    useEffect(() => {
        const map = olMapRef.current;

        const fallbackResponse = {code: 200, data: {markets: []}};

        const loadMarkets = (marketData) => {
            let marketList = marketData.markets || [];
            if (marketList.length > 200) {
                marketList = marketList.slice(0, 200);
            }
            if (userLocation) {
                marketList.forEach((m) => {
                    const lat = parseFloat(m.lat);
                    const lon = parseFloat(m.long);
                    if (!isNaN(lat) && !isNaN(lon)) {
                        const distance = haversine(userLocation.lat, userLocation.lon, lat, lon);
                        m.distance = distance;
                        m.distanceText = formatDistance(distance);
                    }
                });
                marketList.sort((a, b) => a.distance - b.distance);
            }
            setMarkets(marketList);
        };

        const delayDebounceFn = setTimeout(() => {
            setIsSearching(true);
            axios
                .get(apiUrl, {params: {q: query, platform: 'web', map: true}})
                .then((res) => {
                    const apiMarkets = res.data?.data?.markets;
                    if (Array.isArray(apiMarkets)) {
                        loadMarkets({markets: apiMarkets});
                    } else {
                        loadMarkets(fallbackResponse.data);
                    }
                })
                .catch(() => {
                    loadMarkets(fallbackResponse.data);
                })
                .finally(() => {
                    setIsSearching(false);
                });
        }, 400);

        return () => clearTimeout(delayDebounceFn);
    }, [query, userLocation]);

    useEffect(() => {
        const map = olMapRef.current;
        if (!map || markets.length === 0) return;

        if (markerLayerRef.current) {
            map.removeLayer(markerLayerRef.current);
        }

        const features = markets.map((market, index) => {
            const feature = new Feature({
                geometry: new Point(fromLonLat([parseFloat(market.long), parseFloat(market.lat)])),
                marketIndex: index
            });

            feature.setStyle(
                new Style({
                    image: new Icon({
                        src: '/assets/location-icon.png',
                        scale: 0.07,
                        anchor: [0.5, 1]
                    }),
                    text: new Text({
                        text: market.name,
                        font: 'bold 12px IRANSans',
                        fill: new Fill({color: '#000'}),
                        stroke: new Stroke({color: '#fff', width: 2}),
                        offsetY: -35,
                        textAlign: 'center'
                    })
                })
            );

            return feature;
        });

        const vectorSource = new VectorSource({features});
        const markerLayer = new VectorLayer({source: vectorSource});
        map.addLayer(markerLayer);
        markerLayerRef.current = markerLayer;

        map.on('singleclick', (evt) => {
            map.forEachFeatureAtPixel(evt.pixel, (feature) => {
                const idx = feature.get('marketIndex');
                if (typeof idx === 'number') {
                    setSelectedIndex(idx);
                    const el = cardRefs.current[idx];
                    if (el) {
                        el.scrollIntoView({behavior: 'smooth', inline: 'center', block: 'nearest'});
                    }
                }
            });
        });
    }, [markets]);

    const handleCardClick = (index) => {
        setSelectedIndex(index);
        const market = markets[index];
        if (market) {
            const coord = fromLonLat([parseFloat(market.long), parseFloat(market.lat)]);
            olMapRef.current.getView().animate({
                center: coord,
                zoom: 16,
                duration: 800
            });
        }
    };

    const haversine = (lat1, lon1, lat2, lon2) => {
        const R = 6371000;
        const toRad = d => (d * Math.PI) / 180;
        const dLat = toRad(lat2 - lat1);
        const dLon = toRad(lon2 - lon1);
        const a =
            Math.sin(dLat / 2) ** 2 +
            Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
        return Math.round(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
    };

    const formatDistance = (meters) =>
        meters >= 1000 ? `${(meters / 1000).toFixed(1)} کیلومتر` : `${meters} متر`;

    return (
        <div style={{position: 'relative', height: '100dvh'}}>
            <AppBar sx={{background: 'rgba(238,238,238,0.3)'}} position="absolute" elevation={0}>
                <Toolbar>
                    <Box sx={{flexGrow: 1, mx: 1}}>
                        <TextField
                            fullWidth
                            placeholder="جستجوی فروشگاه..."
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            InputProps={{
                                sx: {background: '#f1f1f1', borderRadius: 2},
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon/>
                                    </InputAdornment>
                                ),
                                endAdornment: isSearching && (
                                    <InputAdornment position="end">
                                        <CircularProgress size={20}/>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Box>
                    <IconButton sx={{color: 'black'}} size="large">
                        <BasketComponent/>
                    </IconButton>
                    <BackButton/>
                </Toolbar>
                {isSearching && <LinearProgress color="primary"/>}
            </AppBar>

            <div ref={mapRef} style={{width: '100%', height: '100%'}}/>

            <div style={{position: 'absolute', bottom: 0, left: 0, right: 0, direction: 'rtl', paddingBottom: 10}}>
                <div style={{overflowX: 'auto', display: 'flex', scrollSnapType: 'x mandatory', padding: '8px 12px'}}>
                    {markets.map((market, i) => (
                        <div key={market.id} ref={el => cardRefs.current[i] = el} style={{display: 'inline-block'}}>
                            <MarkerCard
                                market={market}
                                isSelected={i === selectedIndex}
                                onSelect={() => handleCardClick(i)}
                                MARKET_BASE_URL_API={MARKET_BASE_URL_API}
                                defaultMarketImage={defaultMarketImage}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
